import { Injectable, Inject } from '@angular/core';
import { Store, Action, select } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';

import * as actions from '../actions';
import * as selectors from '../selectors';
import * as Utils from '@shared/core/utils';
import * as Tokens from '@shared/core/tokens';
import * as Consts from '@shared/core/consts';

import { Observable, of } from 'rxjs';
import { switchMap, combineLatest, take, filter, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class AvailablePickupsEffects {
    public onCollectionTypeChangeRequestRecalculations$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.SetCollectionType),
            withLatestFrom(
                this._store.pipe(select(selectors.getOrderTypeId)),
                this._store.pipe(select(selectors.getCurrentLocationNo)),
                this._store.pipe(select(selectors.routeIsLocationDetailsPage())),
            ),
            switchMap(([, orderTypeId, locationNo, isLocationDetailsPage]) => {
                if (locationNo && isLocationDetailsPage) {
                    return [actions.AvailablePickupsCalculateRequest({ locationNo, orderTypeId })];
                }

                return [];
            }),
        ),
    );

    /* This effect is triggered from orderingTimeInfo effects when data is downloaded after successful locations request  */
    public calculateAvailablePickupOptionsForLocation: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.AvailablePickupsCalculateRequest),
            switchMap((action) =>
                of(action).pipe(
                    combineLatest(
                        this._store.pipe(
                            select(selectors.getOrderingTimeInfoByLocationNo(action.locationNo)),
                            filter((obj) => obj != null),
                            take(1),
                        ),
                        this._store.pipe(
                            select(selectors.getLocationDetails(action.locationNo)),
                            filter((location) => location != null),
                            take(1),
                        ),
                        this._store.pipe(select(selectors.getSelectedOrderTypeId), take(1)),
                    ),
                    switchMap(([_, openingHours, location, orderTypeId]) => {
                        const orderType = location.OrderTypes.find((obj) => obj.Id === orderTypeId);
                        const collectionTypeConfig = new Utils.CollectionTypeHelper(this._config.collectionTypes).getCollectionTypeByOrderTypeId(orderTypeId);
                        const orderInfo = Utils.LocationPickups.getCompleteOrderInfoByDate({ location, date: null, orderTypeId });

                        const params: OLO.Ordering.GeneratePickupsParams = {
                            orderTypeId: collectionTypeConfig.orderTypeIds[0],
                            location,
                            asapPickupMins: orderInfo.minimumPickupTime,
                            openingHours,
                            schedule: this._config.onlineOrders.scheduledOrders === true,
                            minimumPreparationTime: orderType?.DefaultOnlineOrderPreparationTimeInMinutes ?? Consts.PICKUPS.MINIMUM_PICKUP_TIME,
                            displayAsTimespans: orderType?.AvailablePickupTimes.DisplayAsTimeSpans,
                            orderTimeoutBufferMins: 'orderTimeoutBufferMins' in collectionTypeConfig ? collectionTypeConfig?.orderTimeoutBufferMins : null,
                            startBufferMins: orderType?.AvailablePickupTimes.OrderingTimeStartOffsetInMinutes,
                            nextTick: orderType?.AvailablePickupTimes.NextTickInMinutes,
                            endBufferMins: orderType?.AvailablePickupTimes.OrderingTimeEndOffsetInMinutes,
                            delayAfterAsap: orderType?.AvailablePickupTimes.DelayFirstTickAfterAsapInMinutes,
                        };

                        return [
                            actions.AvailablePickupsCalculateSuccessRequest({
                                locationNo: location.LocationNo,
                                orderTypeId: collectionTypeConfig.orderTypeIds[0],
                                payload: Utils.Pickups.generateTimeSlotsList(params),
                            }),
                        ];
                    }),
                ),
            ),
        ),
    );

    constructor(
        @Inject(Tokens.STATIC_TEXT_TOKEN) public readonly t: T.StaticTexts,
        @Inject(Tokens.CONFIG_TOKEN) private _config: OLO.Config,
        private _actions$: Actions,
        private _store: Store<OLO.State>,
    ) {}
}
